import type {SubscriptionTierFormValues} from "../../../components/offering-form/subscriptions/subscription-tier-form/types";
import type {CreateSubscriptionApiPayload} from "../../../react-query-hooks/store/subscriptions/create-subscription/types";
import type {
  SubscriptionTierEntity,
  UpdateSubscriptionTierItemPayload,
} from "../../../react-query-hooks/store/subscriptions/tiers/types";
import type {UpdateSubscriptionApiPayload} from "../../../react-query-hooks/store/subscriptions/update-subscription";
import {CreateSubscriptionFormValues} from "./schema-validation";
import {
  addTierPayloadToCreateApiTier,
  tierFormRowToAddTierPayload,
} from "../subscription-tier-form-to-api-payload";

function slugFromSubscriptionName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, "-");
}

/**
 * react-hook-form does not register `apiTierId`, so the field can be stripped from
 * `values.tiers`. Recover the id from previously loaded API tiers by matching tier
 * name (trim + lower-case) so existing rows update instead of inserting duplicates.
 */
function resolveExistingTierId(
  tier: SubscriptionTierFormValues & {apiTierId?: number},
  originalTiers: SubscriptionTierEntity[]
): number | undefined {
  if (typeof tier.apiTierId === "number") {
    return tier.apiTierId;
  }
  const formName = (tier.tierName ?? "").trim().toLowerCase();
  if (!formName) {
    return undefined;
  }
  return originalTiers.find((t) => t.name.trim().toLowerCase() === formName)?.id;
}

/**
 * PATCH payload when revisiting step 1 after initial POST create.
 * Sends tiers in the same payload — backend upserts based on `id` presence.
 */
export function createSubscriptionBasicValuesToUpdatePayload(
  values: CreateSubscriptionFormValues,
  originalTiers: SubscriptionTierEntity[] = []
): UpdateSubscriptionApiPayload {
  const tiers: UpdateSubscriptionTierItemPayload[] = (values.tiers ?? []).map(
    (tier, index) => {
      const addPayload = tierFormRowToAddTierPayload(tier, index);
      const item: UpdateSubscriptionTierItemPayload = {
        name: addPayload.name,
        billingInterval: addPayload.billingInterval,
        billingDuration: addPayload.billingDuration,
        billingCycles: addPayload.billingCycles,
        features: addPayload.features,
        sortOrder: addPayload.sortOrder,
        isPayWhatYouWant: addPayload.isPayWhatYouWant,
        ...(addPayload.defaultMinPrice === undefined
          ? {}
          : {defaultMinPrice: addPayload.defaultMinPrice}),
        currencies: addPayload.currencies ?? [],
        ...(addPayload.redirectType
          ? {
              redirectType: addPayload.redirectType,
              telegramGroupChatId: addPayload.telegramGroupChatId,
              urlRedirect: addPayload.urlRedirect,
            }
          : {}),
      };
      const resolvedId = resolveExistingTierId(tier, originalTiers);
      if (typeof resolvedId === "number") {
        item.id = resolvedId;
      }
      return item;
    }
  );

  const payload: UpdateSubscriptionApiPayload = {
    name: (values.name ?? "").trim(),
    description: values.description ?? "",
    hideFromProfile: values.hideFromProfile ?? false,
    requestPhoneNumber: values.requestCustomerPhoneNumber ?? false,
    enableCurrencyConversion: true,
    selectedCurrencies: values.selected_currencies ?? [],
    tiers,
  };
  if (values.isUrlRedirect && values.url_redirect?.trim()) {
    payload.redirectUrl = values.url_redirect.trim();
  }
  return payload;
}

/** Attach API tier ids to form rows (same order as API sortOrder) so PATCH tier sync does not duplicate rows. */
export function mergeSubscriptionTierFormRowsWithApiIds(
  formTiers: SubscriptionTierFormValues[] | undefined,
  apiTiers: SubscriptionTierEntity[] | undefined
): Array<SubscriptionTierFormValues & {apiTierId?: number}> {
  if (!formTiers?.length) {
    return [];
  }
  const sorted =
    apiTiers && apiTiers.length > 0
      ? [...apiTiers].sort((a, b) => a.sortOrder - b.sortOrder)
      : [];
  return formTiers.map((row, index) => ({
    ...row,
    apiTierId: sorted[index]?.id,
  }));
}

/**
 * react-hook-form only submits registered fields — `apiTierId` is never registered, so it is stripped
 * from `values.tiers`. Re-attach ids from the last persisted draft (context) or sync will POST duplicates.
 */
export function restoreSubscriptionTierApiIdsFromDraft(
  submittedTiers: SubscriptionTierFormValues[] | undefined,
  persistedTiers: Array<SubscriptionTierFormValues & {apiTierId?: number}> | undefined
): Array<SubscriptionTierFormValues & {apiTierId?: number}> {
  if (!submittedTiers?.length) {
    return submittedTiers ?? [];
  }
  const persistedList = persistedTiers ?? [];
  const idByTierName = new Map<string, number>();
  for (const row of persistedList) {
    const key = row.tierName.trim().toLowerCase();
    if (typeof row.apiTierId === "number") {
      idByTierName.set(key, row.apiTierId);
    }
  }
  return submittedTiers.map((tier, index) => {
    const submittedId = (tier as {apiTierId?: number}).apiTierId;
    if (typeof submittedId === "number") {
      return {...tier, apiTierId: submittedId};
    }
    const byIndex = persistedList[index]?.apiTierId;
    if (typeof byIndex === "number") {
      return {...tier, apiTierId: byIndex};
    }
    const byName = idByTierName.get(tier.tierName.trim().toLowerCase());
    if (typeof byName === "number") {
      return {...tier, apiTierId: byName};
    }
    return {...tier};
  });
}

export function createSubscriptionDto(
  values: CreateSubscriptionFormValues
): CreateSubscriptionApiPayload {
  const redirectUrl =
    values.isUrlRedirect && values.url_redirect?.trim()
      ? values.url_redirect.trim()
      : undefined;

  const subscriptionName = values.name ?? "";
  const selectedCurrencies = values.selected_currencies ?? [];

  const payload: CreateSubscriptionApiPayload = {
    name: subscriptionName,
    slug: slugFromSubscriptionName(subscriptionName),
    description: values.description ?? "",
    hideFromProfile: values.hideFromProfile ?? false,
    requestPhoneNumber: values.requestCustomerPhoneNumber ?? false,
    bookingQuestions:
      values.bookingQuestions
        ?.map((q) => (typeof q === "string" ? q : q.question))
        .filter(Boolean) ?? [],
    enableCurrencyConversion: true,
    selectedCurrencies,
    tiers: (values.tiers ?? []).map((tier, index: number) =>
      addTierPayloadToCreateApiTier(tierFormRowToAddTierPayload(tier, index))
    ),
  };

  if (redirectUrl) {
    payload.redirectUrl = redirectUrl;
  }

  return payload;
}
